import { toast } from "react-toastify";

// Define the Target type
interface Target {
  position: string;
  size: number;
  gif: string;
  blinkAndResizing: boolean;
}

// Define the TargetGroup type
interface TargetGroup {
  sameShape: boolean;
  targets: Target[];
}

// Define the GenerateOptions type
interface GenerateOptions {
  sizes: number[];
  blinkingAndResizing: boolean;
}

export const generateChampProfondFormesTargets = (
  cases: { [key: string]: string[] },
  gifs: string[],
  options: GenerateOptions,
  module_type: "module_formes" | "module_couleurs"
): TargetGroup[] => {

  const ZF = cases["ZF"];
  const ZP = cases["ZP"];
  
  const ZPZF = [...ZF, ...ZP];
  const targetGroups: TargetGroup[] = [];
  const sizeCounts: Record<number, number> = {};

const getRandomGifPair = (): [string, string, boolean] => {
  let gif2: string;
  const sameShape = Math.random() < 0.5;

  // Tirer gif1 au hasard
   const gif1 = gifs[Math.floor(Math.random() * gifs.length)];
  
  const getRetraitement = (gif: string) => {
    try {
      const parts = gif.split("_");
    // Structure attendue: cat_spec_color_...name
    // On veut:
    // - module_formes: comparer uniquement la forme globale (catégorie + nom),
    //   en ignorant la couleur (parts[2]) et la spécificité (plein/vide/horizontal ou fixe/mouvement - parts[1]).
    // - module_couleurs: comparer uniquement la couleur.
    if (parts.length < 4) return gif; // fallback robuste

    const [ , spec, color, ...rest] = parts;
    const name = rest.join("_");

    if (module_type === "module_formes") {
      // Ex: geometrie_plein_bleu_carre -> geometrie_carre
      //     objet_fixe_jaune_voiture -> objet_voiture
      if (name === "trait.png") {
        // Pour les traits, on inclut la spécificité (plein/vide/horizontal/diagonal/vertical)
        console.log("trait detected:", `${spec}_${name}`);
        return `${spec}_${name}`;
      } 
      return `${name}`;
    } else if (module_type === "module_couleurs") {
      // On ne compare que la couleur
      return color;
    }
    return "";
    } catch {
      toast.error("Erreur lors de la comparaison des formes/couleurs des GIFs.");
      return gif;
    }
  };

  // const getRetraitementGifSpec_Name = (gif: string) => {
  //   try {
  //     const parts = gif.split("_");
  //     // Structure attendue: cat_spec_color_...name
  //     // On veut:
  //     // - module_formes: comparer uniquement la forme globale (catégorie + nom),
  //     //   en ignorant la couleur (parts[2]) et la spécificité (plein/vide/horizontal ou fixe/mouvement - parts[1]).
  //     // - module_couleurs: comparer uniquement la couleur.
  //     if (parts.length < 4) return gif; // fallback robuste
  //     const [ , spec, color, ...rest] = parts;
  //     // console.log(' parts:', parts, 'cat:', cat, 'color:', color, 'spec:', spec);
  //     const name = rest.join("_");
  //     if (module_type === "module_formes") {
  //       // Ex: geometrie_plein_bleu_carre -> geometrie_carre  
  //       //     objet_fixe_jaune_voiture -> objet_voiture
  //       return `${spec}_${name}`;
  //     } else if (module_type === "module_couleurs") {
  //       // On ne compare que la couleur
  //       return color;
  //     } return "";
  //   } catch {
  //     toast.error("Erreur lors de la comparaison des formes/couleurs des GIFs.");
  //     return gif;
  //   }
  // };

  const gif1Retraite = getRetraitement(gif1);

  // Tirer gif2 selon les critères du schéma
  do {
    gif2 = gifs[Math.floor(Math.random() * gifs.length)];
  } while (
    (sameShape && getRetraitement(gif2) !== gif1Retraite ) || // on veut une égalité
    (!sameShape && getRetraitement(gif2) === gif1Retraite)   // on veut une différence
  );
    // if (sameShape &&module_type === "module_formes" && (getRetraitement(gif1) === 'trait.png' || getRetraitement(gif1) === 'trait.png')) {
    //   console.log("2:", getRetraitementGifSpec_Name(gif2), "1:", getRetraitementGifSpec_Name(gif1),getRetraitement(gif2), gif1Retraite, getRetraitement(gif2) === gif1Retraite, 'sameShape', sameShape,(sameShape && getRetraitement(gif2) !== gif1Retraite) ||  (!sameShape && getRetraitement(gif2) === gif1Retraite));
    //   console.log("generated pair:", gif1, gif2, sameShape);
    // }
  return [gif1, gif2, sameShape];
};


  const generateTarget = (
    position: string,
    size: number,
    gif: string,
    blinkAndResizing: boolean
  ): Target => {
    return {
      position,
      size,
      gif,
      blinkAndResizing,
    };
  };

  const generateSymmetricalPosition = (
    position: string,
    size: number
  ): string | null => {
    const col = position.charAt(0);
    const row = position.substring(1);
    const colIndex = col.charCodeAt(0) - "A".charCodeAt(0);
    const symmetricalColIndex = 15 - colIndex - (size - 1); // Adjusting for size
    if (symmetricalColIndex < 0 || symmetricalColIndex > 15) return null;
    const symmetricalCol = String.fromCharCode(
      "A".charCodeAt(0) + symmetricalColIndex
    );
    return `${symmetricalCol}${row}`;
  };

const generateAllTargets = (
    positions: string[],
    size: number,
    blinkAndResizing: boolean
  ): boolean => {
    const [gif1, gif2, sameShape] = getRandomGifPair();
    const symmetricalPositions = positions
      .map((pos) => generateSymmetricalPosition(pos, size))
      .filter((p) => p !== null) as string[];

    // 30% sans mouvement, 70% avec mouvement (seed, miroir ou les deux)
    const animEnabled = blinkAndResizing && Math.random() < 0.7;
    let seedBlinks = false;
    let mirrorBlinks = false;
    if (animEnabled) {
      const mode = Math.random();
      if (mode < 1 / 3) {
        seedBlinks = true;
      } else if (mode < 2 / 3) {
        mirrorBlinks = true;
      } else {
        seedBlinks = true;
        mirrorBlinks = true;
      }
    }

    const targets: Target[] = positions.map((position) =>
      generateTarget(position, size, gif1, seedBlinks)
    );
    const symmetricalTargets: Target[] = symmetricalPositions.map((position) =>
      generateTarget(position, size, gif2, mirrorBlinks)
    );

    if (targets.length === symmetricalTargets.length && targets.length > 0) {
      targetGroups.push({
        sameShape,
        targets: [targets[0], symmetricalTargets[0]],
      });
      sizeCounts[size] = (sizeCounts[size] || 0) + 1;
      return true;
    }
    return false;
  };

  const generatePositions = (
    startCol: string,
    startRow: number,
    size: number,
    validPositions: string[]
  ): string[] => {
    const positions: string[] = [];

    for (let i = 0; i < size; i++) {
      for (let j = 0; j < size; j++) {
        const pos = `${String.fromCharCode(startCol.charCodeAt(0) + i)}${
          startRow + j
        }`;
        if (validPositions.includes(pos)) {
          positions.push(pos);
        } else {
          return []; // Si une des positions n'est pas valide, on arrête et retourne un tableau vide.
        }
      }
    }

    return positions;
  };

  // A position set is valid if it stays entirely in one hemifield (A–H or I–P).
  const isValidPosition = (positions: string[]): boolean => {
    if (positions.length === 0) return false;
    const cols = positions.map((p) => p.charCodeAt(0) - "A".charCodeAt(0));
    const minColIndex = Math.min(...cols);
    const maxColIndex = Math.max(...cols);
    // Entièrement à gauche (<= H) OU entièrement à droite (>= I)
    return maxColIndex <= 7 || minColIndex >= 8;
  };

  const attemptGenerateTargetsWithSize = (pos: string, size: number): boolean => {
    if (size < 1) {
      return false;
    }
    const col = pos.charAt(0);
    const row = parseInt(pos.substring(1), 10);
    const blinkAndResizing = options.blinkingAndResizing;

    const positions = generatePositions(col, row, size, ZPZF); // Passer ZPZF comme positions valides
    const symmetricalPositions = positions
      .map((pos) => generateSymmetricalPosition(pos, size))
      .filter((p) => p !== null) as string[];

    if (
      positions.length === size * size && // toutes les cellules existent côté gauche
      symmetricalPositions.length === size * size && // et côté droit
      isValidPosition(positions) // ne mord pas la ligne centrale
    ) {
      return generateAllTargets(positions, size, blinkAndResizing);
    }
    return false;
  };

  // Assurer une variété de tailles : on cycle les tailles fournies plutôt que de privilégier toujours la plus grande
  const sizesPool = Array.from(new Set(options.sizes)); // déduplication éventuelle
  let sizeCycleIndex = 0;
  for (const pos of ZPZF) {
    // Seed possible des deux hémichamps
    let generated = false;
    // On tente chaque taille en commençant par une taille différente à chaque position (rotation)
    for (let attempt = 0; attempt < sizesPool.length && !generated; attempt++) {
      const size = sizesPool[(sizeCycleIndex + attempt) % sizesPool.length];
      generated = attemptGenerateTargetsWithSize(pos, size);
    }
    sizeCycleIndex = (sizeCycleIndex + 1) % sizesPool.length;
  }

  if (targetGroups.length === 0) {
    for (const pos of ZPZF) {
      if (attemptGenerateTargetsWithSize(pos, 1)) break;
    }
  }
  console.log(`Generated targets ${module_type} Formes:`, targetGroups);

  return targetGroups;
};

// // Example usage
// const cases = {
//   "ZF": [
//     "A1", "B1", "C1", "D1", "E1", "F1", "G1", "H1",
//     "A2", "B2", "C2", "D2", "E2", "F2", "G2", "H2",
//     "A3", "B3", "C3", "D3", "E3", "F3", "G3", "H3",
//     "A4", "B4", "C4", "D4", "E4", "F4", "G4", "H4"
//   ],
//   "ZP": [
//     "I1", "J1", "K1", "L1", "M1", "N1", "O1", "P1",
//     "I2", "J2", "K2", "L2", "M2", "N2", "O2", "P2",
//     "I3", "J3", "K3", "L3", "M3", "N3", "O3", "P3",
//     "I4", "J4", "K4", "L4", "M4", "N4", "O4", "P4"
//   ],
//   "ZV": [
//     "A5", "B5", "C5", "D5", "E5", "F5", "G5", "H5",
//     "I5", "J5", "K5", "L5", "M5", "N5", "O5", "P5",
//     "A6", "B6", "C6", "D6", "E6", "F6", "G6", "H6",
//     "I6", "J6", "K6", "L6", "M6", "N6", "O6", "P6",
//     "A7", "B7", "C7", "D7", "E7", "F7", "G7", "H7",
//     "I7", "J7", "K7", "L7", "M7", "N7", "O7", "P7",
//     "A8", "B8", "C8", "D8", "E8", "F8", "G8", "H8",
//     "I8", "J8", "K8", "L8", "M8", "N8", "O8", "P8"
//   ]
// };

// const gifs = [
//   "https://via.placeholder.com/50x50",
//   "https://via.placeholder.com/50x50?text=1",
//   "https://via.placeholder.com/50x50?text=2",
//   "https://via.placeholder.com/50x50?text=3",
//   "https://via.placeholder.com/50x50?text=4",
//   "https://via.placeholder.com/50x50?text=5",
//   "https://via.placeholder.com/50x50?text=6",
//   "https://via.placeholder.com/50x50?text=7",
//   "https://via.placeholder.com/50x50?text=8",
//   "https://via.placeholder.com/50x50?text=9",
//   "https://via.placeholder.com/50x50?text=10"
// ];

// const options: GenerateOptions = {
//   blinkingAndResizing: true,
//   sizes: [1, 2, 3]
// };

// const targets = generateChampProfondFormesTargets(cases, gifs, options);
// console.log(JSON.stringify(targets, null, 2));
